import { memo } from "react"

import TonIcon from "@/assets/icons/ton-icon.svg?react"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import { cn } from "@/shared/lib/utils"
import type { UserGift } from "@/shared/api/types"

interface GiftCardProps {
  gift: UserGift
  onClick?: (gift: UserGift) => void
}

function getDisplayName(gift: UserGift): string {
  if (gift.collection && typeof gift.num === "number") {
    return `${gift.collection} #${gift.num}`
  }
  if (gift.collection) return gift.collection
  if (typeof gift.num === "number") return `Gift #${gift.num}`
  return gift.slug
}

function GiftCardBase({ gift, onClick }: GiftCardProps) {
  const name = getDisplayName(gift)
  const price = formatTonFromNano(gift.assessed_price_nano)
  const interactive = typeof onClick === "function"

  return (
    <button
      type="button"
      onClick={interactive ? () => onClick(gift) : undefined}
      disabled={!interactive}
      className={cn(
        "flex w-full flex-col items-center gap-1 focus:outline-none",
        interactive
          ? "transition-transform active:scale-[0.97]"
          : "cursor-default"
      )}
      aria-label={name}
    >
      <div className="relative w-full pb-2.5">
        <div
          className="h-28 w-full overflow-hidden rounded-2xl bg-secondary-main bg-cover bg-center"
          style={{ backgroundImage: `url(${gift.small_image_url})` }}
        />
        <div className="absolute bottom-0 left-1/2 flex h-5 -translate-x-1/2 items-center gap-0.5 rounded-full bg-white px-1.5">
          <span className="text-[14px] font-medium leading-4 tracking-[-0.5px] text-black-100">
            {price}
          </span>
          <TonIcon className="size-3.5 text-black-100" />
        </div>
      </div>
      <span className="line-clamp-1 w-full text-center text-[14px] font-medium leading-4 tracking-[-0.5px] text-white">
        {name}
      </span>
    </button>
  )
}

export const GiftCard = memo(GiftCardBase)
